import { Navigate } from 'react-router-dom';
import { useFeatures } from '../../hooks/useFeatures';

/**
 * FeatureRoute - Protects routes based on organization feature access
 *
 * This component checks if the user's organization has access to a specific feature.
 * If the feature is disabled, it redirects to the dashboard silently.
 *
 * Fails closed while feature access is loading so protected content is not exposed
 * before the organization's entitlement is resolved.
 *
 * Usage:
 * <FeatureRoute feature="messages">
 *   <CommsPage />
 * </FeatureRoute>
 *
 * @param {Object} props
 * @param {string} props.feature - The feature type to check (e.g., 'messages', 'documents')
 * @param {React.ReactNode} props.children - The content to render if feature is enabled
 * @param {string} props.redirectTo - Where to redirect if feature is disabled (default: '/dashboard')
 */
export const FeatureRoute = ({
  feature,
  children,
  redirectTo = '/dashboard',
}) => {
  const { hasFeature, isLoading, error } = useFeatures();

  // Keep protected content hidden until the entitlement check resolves.
  if (isLoading) {
    return null;
  }

  // If there's an error fetching features, fail-closed by redirecting (deny access)
  if (error) {
    console.error('Feature check failed, redirecting to dashboard:', error);
    return <Navigate to={redirectTo} replace />;
  }

  // Check if feature is enabled
  if (!hasFeature(feature)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Feature is enabled, render children
  return children;
};

export default FeatureRoute;
